/**
 * Page View Tracking Hook
 * 
 * 페이지 조회를 자동으로 Google Analytics에 추적하는 훅입니다.
 * React Router의 location 변경을 감지하여 페이지뷰를 전송합니다.
 */

import { useEffect } from "react";
import { useLocation } from "react-router";

import trackEvent from "~/core/lib/analytics.client";

/**
 * 페이지 조회를 자동으로 추적하는 커스텀 훅
 * 
 * 이 훅은 컴포넌트에서 호출하면 페이지 경로가 변경될 때마다
 * Google Analytics에 페이지뷰 이벤트를 자동으로 전송합니다.
 * 
 * @example
 * ```tsx
 * function App() {
 *   usePageTracking();
 *   return <Outlet />;
 * }
 * ```
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    // 페이지 조회 이벤트 전송
    trackEvent("page_view", {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
    });

    // 디버그 로그 (개발 환경에서만)
    if (import.meta.env.DEV) {
      console.log("📊 [Analytics] Page View:", {
        path: location.pathname,
        title: document.title,
      });
    }
  }, [location.pathname]); // pathname이 변경될 때마다 실행
}

/**
 * 특정 페이지 섹션 조회를 추적하는 함수
 * 
 * SPA에서 페이지 내부 섹션 이동을 추적할 때 사용합니다.
 * 
 * @example
 * ```tsx
 * function ScrollToSection({ section }: { section: string }) {
 *   const handleClick = () => {
 *     trackSectionView(section);
 *     // ... 스크롤 로직
 *   };
 * }
 * ```
 */
export function trackSectionView(sectionName: string) {
  trackEvent("section_view", {
    section_name: sectionName,
    page_path: window.location.pathname,
  });
}

